import type {
  BatchResultStatus,
  ContractFunctionResult,
} from "@eulerxyz/evc-sdk"
import type { Address, Hex } from "viem"
import {
  Asset,
  TransactionItem,
  TransactionLabel,
} from "./baseTransactionTypes"

export type SwapTransactionLabels = TransactionLabel.SWAP

export type SwapHubTransactionLabels = TransactionLabel.LEVERAGE //?? not sure if this is correct

export enum SwapHandler {
  GENERIC = "Generic",
  UNISWAP_V2 = "uniswapV2",
  UNISWAP_V3 = "uniswapV3",
}

export type BaseSwapTransaction<item> = {
  label: TransactionLabel.SWAP
  from: Address
  to: Address
  asset: Asset
  outputAsset: Asset
  outputValue: string
  value: string
  result?: BatchResultStatus<ContractFunctionResult>
} & item

export enum SwapperMode {
  // 0 - exact input swap
  EXACT_IN = 0,
  // 1 - exact output swap
  EXACT_OUT = 1,
  // 2 - exact output swap and repay, targeting a debt amount of an account
  TARGET_DEBT = 2,
}

export type SwapperTransactionBase = {
  mode: SwapperMode
  vaultIn: Address
  accountIn: Address
  data: Hex
  subLabel?: string
}

export type SwapperBaseTransactionItem =
  BaseSwapTransaction<SwapperTransactionBase>

export type UniswapV3SwapTransactionItem = SwapperBaseTransactionItem & {
  swapHandler: SwapHandler.UNISWAP_V3
}

export function isUniswapV3SwapTransactionItem(
  transaction: SwapTransactionItem,
): transaction is UniswapV3SwapTransactionItem {
  return (
    transaction.label === TransactionLabel.SWAP &&
    transaction.swapHandler === SwapHandler.UNISWAP_V3
  )
}

export type UniswapV2SwapTransactionItem = SwapperBaseTransactionItem & {
  swapHandler: SwapHandler.UNISWAP_V2
}

export function isUniswapV2SwapTransactionItem(
  transaction: SwapTransactionItem,
): transaction is UniswapV2SwapTransactionItem {
  return (
    transaction.label === TransactionLabel.SWAP &&
    transaction.swapHandler === SwapHandler.UNISWAP_V2
  )
}

export type GenericSwapTransactionItem = SwapperBaseTransactionItem & {
  swapHandler: SwapHandler.GENERIC
}

export function isGenericSwapTransactionItem(
  transaction: SwapTransactionItem,
): transaction is GenericSwapTransactionItem {
  return (
    transaction.label === TransactionLabel.SWAP &&
    transaction.swapHandler === SwapHandler.GENERIC
  )
}

export type SwapMulticallTransactionItem = {
  label: TransactionLabel.SWAP_MULTICALL
  subLabel?: string
  from: Address
  to: Address
  asset: Asset
  fromVault: Address
  toVault: Address
  accountTo: Address
  receiver: Address
  assetIn: Asset
  assetOut: Asset
  amountIn: string
  amountOut: string
  swapperAddress: Address
  swapperData: Hex
  calls: Hex[]
  result?: BatchResultStatus<ContractFunctionResult>
}

export function isSwapMulticallTransactionItem(
  transaction: TransactionItem,
): transaction is SwapMulticallTransactionItem {
  return transaction.label === TransactionLabel.SWAP_MULTICALL
}

export type SwapRepayAndDepositTransactionItem = {
  label: TransactionLabel.SWAP_REPAY_AND_DEPOSIT
  subLabel?: string
  from: Address
  to: Address
  asset: Asset
  value: string
  repayAmount: string
  vaultIn: Address
  result?: BatchResultStatus<ContractFunctionResult>
}

export function isSwapRepayAndDepositTransactionItem(
  transaction: SwapTransactionItem,
): transaction is SwapRepayAndDepositTransactionItem {
  return transaction.label === TransactionLabel.SWAP_REPAY_AND_DEPOSIT
}

export type SwapDepositTransactionItem = {
  label: TransactionLabel.SWAP_DEPOSIT
  subLabel?: string
  from: Address
  to: Address
  asset: Asset
  value: string
  vaultIn: Address
  result?: BatchResultStatus<ContractFunctionResult>
}

export function isSwapDepositTransactionItem(
  transaction: SwapTransactionItem,
): transaction is SwapDepositTransactionItem {
  return transaction.label === TransactionLabel.SWAP_DEPOSIT
}

export type SwapSweepTransactionItem = {
  label: TransactionLabel.SWAP_SWEEP
  subLabel?: string
  from: Address
  to: Address
  asset: Asset
  value: string
  result?: BatchResultStatus<ContractFunctionResult>
}

export function isSwapSweepTransactionItem(
  transaction: any,
): transaction is SwapSweepTransactionItem {
  return transaction.label === TransactionLabel.SWAP_SWEEP
}

export type SwapBorrowAndRepayTransactionItem = {
  label: TransactionLabel.SWAP_BORROW_AND_REPAY
  from: Address
  to: Address
  value: string
  asset: Asset
  assetIn: Asset
  assetOut: Asset
  fromVault: Address
  toVault: Address
  result?: BatchResultStatus<ContractFunctionResult>
  subLabel?: string
}
// export type SwapMulticallTransaction = SwapMulticallTransactionItem
// TODO add other Swapper helpers

export type SwapSingleTransactionItem =
  | UniswapV2SwapTransactionItem
  | UniswapV3SwapTransactionItem
  | GenericSwapTransactionItem
  | SwapRepayAndDepositTransactionItem
  | SwapDepositTransactionItem
  | SwapSweepTransactionItem
  | SwapBorrowAndRepayTransactionItem

export type SwapTransactionItem =
  | SwapSingleTransactionItem
  | SwapMulticallTransactionItem

export type SwapTransaction = SwapTransactionItem
